import type { DomainDocument } from '@myplayer/schema';
import { IconAlertCircle, IconCheck, IconEdit, IconRefresh, IconTrash } from '@myplayer/ui';
import { useState, type ReactElement } from 'react';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardRow,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildCustomDomainContextMenuItems } from './context-menu-items.js';

export interface CustomDomainItem extends Omit<DomainDocument, 'status'> {
  readonly status: DomainDocument['status'] | 'PENDING_DNS' | 'FAILING';
  readonly plainStatus?: 'Pending' | 'Verified' | 'Failed';
  readonly failureReason?: string | null;
}

export type DomainUiStatusLabel =
  'Pending DNS' | 'Setting up HTTPS' | 'Active' | 'FAILING' | 'Failed';

export function formatDomainStatusLabel(status: string, tlsStatus?: string): DomainUiStatusLabel {
  if (status === 'FAILING') return 'FAILING';
  if (status === 'FAILED' || tlsStatus === 'FAILED') return 'Failed';
  if (status === 'ACTIVE' && tlsStatus !== 'PENDING') return 'Active';
  if (status === 'VERIFIED' && tlsStatus === 'ACTIVE') return 'Active';
  if (
    status === 'PROVISIONING_TLS' ||
    status === 'VERIFIED' ||
    (status === 'ACTIVE' && tlsStatus === 'PENDING')
  ) {
    return 'Setting up HTTPS';
  }
  return 'Pending DNS';
}

export function formatDomainStatusVariant(
  status: string,
  tlsStatus?: string,
): 'success' | 'destructive' | 'warning' {
  const label = formatDomainStatusLabel(status, tlsStatus);
  if (label === 'Active') return 'success';
  if (label === 'Failed' || label === 'FAILING') return 'destructive';
  return 'warning';
}

export function getDomainStatusHelpText(domain: CustomDomainItem): string {
  const label = formatDomainStatusLabel(domain.status, domain.tlsStatus);
  if (label === 'Active')
    return 'Active · Verified and HTTPS is live for QR codes using this domain.';
  if (label === 'Setting up HTTPS') {
    return 'Verified · Your DNS records are confirmed and we are finishing automatic HTTPS setup.';
  }
  if (label === 'FAILING' || label === 'Failed') {
    return 'Update your DNS records to match the server values below, then click Re-check.';
  }
  return 'Pending · Add the server-provided DNS records at your domain provider, then click Re-check.';
}

export function formatTlsStatusLabel(tlsStatus: string): string {
  if (tlsStatus === 'ACTIVE') return 'Active';
  if (tlsStatus === 'FAILED') return 'Certificate error';
  return 'Setting up HTTPS';
}

export function getDomainFailureReason(domain: CustomDomainItem): string | null {
  if (domain.status !== 'FAILING' && domain.status !== 'FAILED' && domain.tlsStatus !== 'FAILED') {
    return null;
  }
  const explicitReason = domain.failureReason?.trim();
  if (explicitReason && explicitReason.length > 0) return explicitReason;
  if (domain.tlsStatus === 'FAILED' && domain.status !== 'FAILED' && domain.status !== 'FAILING') {
    const target = domain.cnameTarget?.trim();
    return target
      ? `HTTPS certificate setup failed. Make sure your CNAME record points ${domain.hostname} to ${target}.`
      : `HTTPS certificate setup failed for ${domain.hostname}.`;
  }
  const expectedTxtHost = domain.txtHost?.trim();
  return expectedTxtHost
    ? `DNS TXT record ${expectedTxtHost} was not found or did not match the expected value.`
    : `DNS verification records for ${domain.hostname} were not found or did not match the server values.`;
}

function CopyableRecordField({
  label,
  value,
  copyTestId,
  copyAriaLabel,
}: {
  readonly label: string;
  readonly value: string;
  readonly copyTestId: string;
  readonly copyAriaLabel: string;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  const handleCopy = (): void => {
    void navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-1.5">
      <div className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium text-fg-secondary">{label}</span>
        <code className="mt-0.5 block break-all font-mono text-[12px] font-semibold text-fg">
          {value}
        </code>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="xs"
        data-testid={copyTestId}
        aria-label={copyAriaLabel}
        onClick={handleCopy}
        className="shrink-0 gap-1"
      >
        {copied ? (
          <>
            <IconCheck />
            <span>Copied</span>
          </>
        ) : (
          <span>Copy</span>
        )}
      </Button>
    </div>
  );
}

export function DnsRecordsPanel({
  hostname,
  txtHost,
  verificationToken,
  cnameTarget,
  acmeChallengeRecord,
  testIdPrefix = 'dns-records',
}: {
  readonly hostname: string;
  readonly txtHost?: string;
  readonly verificationToken?: string;
  readonly cnameTarget?: string;
  readonly acmeChallengeRecord?: { readonly name: string; readonly data: string } | null;
  readonly testIdPrefix?: string;
}): ReactElement {
  const effectiveTxtHost = txtHost?.trim() ?? '';
  const effectiveToken = verificationToken?.trim() ?? '';
  const effectiveCnameTarget = cnameTarget?.trim() ?? '';
  const cleanHost = hostname.trim().toLowerCase().replace(/\.+$/, '');
  const effectiveAcme = acmeChallengeRecord ?? {
    name: `_acme-challenge.${cleanHost}.`,
    data: `${cleanHost.replace(/[^a-z0-9-]/g, '-')}.authorize.certificatemanager.goog.`,
  };
  return (
    <div
      data-testid={`${testIdPrefix}-${hostname}`}
      className="rounded-control border border-border bg-surface p-3 text-[12px]"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-fg">DNS records to create</span>
        <span className="text-[11px] text-fg-secondary">
          Add these server-provided records at your DNS provider, then click Re-check
        </span>
      </div>
      <div className="divide-y divide-subtle border-t border-subtle">
        {effectiveTxtHost.length > 0 && (
          <CopyableRecordField
            label="TXT record name (Host)"
            value={effectiveTxtHost}
            copyTestId={`copy-txt-host-${hostname}`}
            copyAriaLabel={`Copy TXT record host for ${hostname}`}
          />
        )}
        {effectiveToken.length > 0 && (
          <CopyableRecordField
            label="TXT record value (Verification token)"
            value={effectiveToken}
            copyTestId={`copy-dns-record-${hostname}`}
            copyAriaLabel={`Copy DNS record for ${hostname}`}
          />
        )}
        {effectiveCnameTarget.length > 0 && (
          <CopyableRecordField
            label={`CNAME record target (points ${hostname} to resolver)`}
            value={effectiveCnameTarget}
            copyTestId={`copy-cname-target-${hostname}`}
            copyAriaLabel={`Copy CNAME target for ${hostname}`}
          />
        )}
        <CopyableRecordField
          label="ACME CNAME record name (TLS certificate challenge)"
          value={effectiveAcme.name}
          copyTestId={`copy-acme-host-${hostname}`}
          copyAriaLabel={`Copy ACME CNAME record host for ${hostname}`}
        />
        <CopyableRecordField
          label="ACME CNAME record target"
          value={effectiveAcme.data}
          copyTestId={`copy-acme-target-${hostname}`}
          copyAriaLabel={`Copy ACME CNAME target for ${hostname}`}
        />
      </div>
    </div>
  );
}

export function DomainFailureBanner({
  hostname,
  reason,
  statusLabel = 'Failed',
}: {
  readonly hostname: string;
  readonly reason: string;
  readonly statusLabel?: 'Failed' | 'FAILING';
}): ReactElement {
  return (
    <div
      role="alert"
      data-testid={`domain-failure-reason-${hostname}`}
      className="flex items-start gap-2 rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
    >
      <IconAlertCircle className="mt-0.5 shrink-0" />
      <div>
        <span className="font-semibold">{statusLabel}: </span>
        <span>{reason}</span>
      </div>
    </div>
  );
}

export function DomainTileCard({
  domain,
  canVerifyDomains,
  canManageDomains,
  submitting,
  onVerify,
  onRecheck,
  onEdit,
  onRequestRemove,
}: {
  readonly domain: CustomDomainItem;
  readonly effectiveHostname?: string;
  readonly canVerifyDomains: boolean;
  readonly canManageDomains: boolean;
  readonly submitting: boolean;
  readonly onVerify: (domain: CustomDomainItem) => void;
  readonly onRecheck?: (domain: CustomDomainItem) => void;
  readonly onEdit: (domain: CustomDomainItem) => void;
  readonly onRequestRemove?: (domain: CustomDomainItem) => void;
}): ReactElement {
  const statusLabel = formatDomainStatusLabel(domain.status, domain.tlsStatus);
  const statusHelpText = getDomainStatusHelpText(domain);
  const failureReason = getDomainFailureReason(domain);
  const isFailing = domain.status === 'FAILING';
  const showRecheckButton = isFailing || statusLabel === 'Active';
  const { bindContextMenu, contextMenuElement } = useContextMenu();

  return (
    <Card
      data-testid={`domain-tile-${domain.hostname}`}
      onContextMenu={bindContextMenu(() =>
        buildCustomDomainContextMenuItems({
          domain,
          canVerifyDomains,
          canManageDomains,
          submitting,
          onVerify,
          onRecheck,
          onEdit,
          onRequestRemove,
        }),
      )}
      className="border border-border bg-surface-subtle shadow-none"
    >
      <CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="font-mono text-[14px] font-semibold text-fg">{domain.hostname}</span>
          <Badge variant={formatDomainStatusVariant(domain.status, domain.tlsStatus)}>
            {statusLabel}
          </Badge>
          {domain.isPrimary && <Badge variant="valid">Primary</Badge>}
        </div>
        {(canVerifyDomains || canManageDomains) && (
          <div className="flex items-center gap-1.5">
            {canVerifyDomains && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={submitting}
                data-testid={`recheck-domain-btn-${domain.hostname}`}
                aria-label={`Re-check ${domain.hostname}`}
                onClick={() =>
                  showRecheckButton ? (onRecheck ?? onVerify)(domain) : onVerify(domain)
                }
                className="gap-1.5"
              >
                <IconRefresh />
                <span>Re-check</span>
              </Button>
            )}
            {canManageDomains && !canVerifyDomains && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={submitting}
                data-testid={`edit-domain-btn-${domain.hostname}`}
                aria-label={`Edit ${domain.hostname}`}
                onClick={() => onEdit(domain)}
                className="gap-1.5"
              >
                <IconEdit />
                <span>Edit</span>
              </Button>
            )}
            {canManageDomains && (
              <ActionOverflowMenu
                label={`More actions for ${domain.hostname}`}
                disabled={submitting}
                data-testid={`domain-overflow-${domain.hostname}`}
              >
                {canVerifyDomains && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={submitting}
                    data-testid={`edit-domain-btn-${domain.hostname}`}
                    aria-label={`Edit ${domain.hostname}`}
                    onClick={() => onEdit(domain)}
                    className="gap-1.5"
                  >
                    <IconEdit />
                    <span>Edit</span>
                  </Button>
                )}
                {onRequestRemove && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={submitting}
                    data-testid={`remove-domain-btn-${domain.hostname}`}
                    aria-label={`Remove domain ${domain.hostname}`}
                    onClick={() => onRequestRemove(domain)}
                    className="gap-1.5"
                  >
                    <IconTrash />
                    <span>Remove domain</span>
                  </Button>
                )}
              </ActionOverflowMenu>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {failureReason !== null && (
          <DomainFailureBanner
            hostname={domain.hostname}
            reason={failureReason}
            statusLabel={isFailing ? 'FAILING' : 'Failed'}
          />
        )}
        <p
          data-testid={`domain-help-${domain.hostname}`}
          className="text-[12px] leading-relaxed text-fg-secondary"
        >
          {statusHelpText}
        </p>
        <div className="divide-y divide-subtle border-t border-subtle">
          <CardRow label="Status">
            <span className="text-[12px] text-fg">
              {failureReason !== null
                ? `${isFailing ? 'FAILING' : 'Failed'} — ${failureReason}`
                : statusLabel}
            </span>
          </CardRow>
          <CardRow label="HTTPS certificate">
            <Badge variant={domain.tlsStatus === 'ACTIVE' ? 'valid' : 'outline'}>
              {formatTlsStatusLabel(domain.tlsStatus)}
            </Badge>
          </CardRow>
          <CardRow label="Routing role">
            <span className="text-[12px] text-fg-secondary">
              {domain.isPrimary ? 'Primary domain for new QR codes' : 'Secondary brand domain'}
            </span>
          </CardRow>
        </div>
        <DnsRecordsPanel
          hostname={domain.hostname}
          {...(domain.txtHost !== undefined ? { txtHost: domain.txtHost } : {})}
          verificationToken={domain.verificationToken}
          {...(domain.cnameTarget !== undefined ? { cnameTarget: domain.cnameTarget } : {})}
          {...(domain.acmeChallengeRecord !== undefined
            ? { acmeChallengeRecord: domain.acmeChallengeRecord }
            : {})}
        />
        {contextMenuElement}
      </CardContent>
    </Card>
  );
}
